import dig from '../util/dig';
import pluralize from '../string/maybePluralize';
import titleize from '../string/titleize';
import formattedPrice from '../util/formattedPrice';
import courseListItemUI from '../../templates/course_list_item.mustache';
import courseListCardUI from '../../templates/course_list_card.mustache';
import Marked from 'marked';

/**
  * Parse and extract the image data from the app search result payload
  *
  * @param {object} app_search result object
  */
export const cardImageData = ({images}) => {
  const imgs = dig( ['product', 'responsive_paths'], images) || {};

  const srcset = Object.keys(imgs)
    .map(key => `${imgs[key]} ${key}`)
    .join(',');
  const src = imgs['1x'];
  return { course_image_srcset: srcset, course_image: src };
};

/**
  * Filters tags (parent_group) for curation_lists and formats
  * for display
  */
export const curatedListTags = ({parent_group}) => {
  const marker = 'curation_list-';
  const articles = ['a', 'and', 'but', 'or', 'of', 'the', 'to'];

  if (parent_group) {
    return parent_group.filter(tag => tag.startsWith(marker) && tag !== `${marker}varsity_tutors_for_schools`)
      .map(t => {
        const target = t.replace(marker,'').replace(/_/g, '-');
        const name = titleize(t.replace(marker,''), articles);
        return { name: name, target: target};
      }).slice(0,4);

  } else {
    return [];
  }
};

/**
  * Returns formatted output of session duration
  *
  * @param {number} minutes
  */
const sessionMinutesOrHours = (minutes) => {
  if (minutes < 60){
    return `${minutes}min`;
  } else if (minutes % 60 == 0) {
    return `${minutes/60}hr`;
  } else {
    return `${Math.floor(minutes/60)}hr ${minutes % 60}min`;
  }
};

const otherOfferings = ({start_dates}) => {
  if (!start_dates) return '';

  const now = new Date().toISOString();
  const future = start_dates.filter((date) => (date > now)).length - 1;
  if (future > 0) {
    return `+ ${future} more class options`;
  } else {
    return '';
  }
};
/**
  * Returns formatted duration string for display
  *
  * @param {object} course object
  */
export const formattedDuration = ({session_length_minutes, session_count}) => {
  const sessionDuration = sessionMinutesOrHours(session_length_minutes);
  const sessionOrSessions = session_count == 1 ? 'session' : 'sessions';

  return `${sessionDuration} live ${sessionOrSessions}`;
};

/**
  * Returns formatted meeting schedule description
  *
  * @param {object} course object
  */
export const formattedMeeting = ({week_count, session_per_week}) => {
  if(week_count == 0){
    return '1 session only';
  }

  const weeks = pluralize(week_count, 'week');
  const sessions = pluralize(session_per_week, 'session');
  const frequency = week_count > 1 ? ' per week' : '';

  return `${weeks}, ${sessions}${frequency}`;
};

/**
  * Returns formatted weekly session
  *
  * @param {object} course object
  */
export const formattedWeeklyDuration = ({week_count, session_per_week, session_length_minutes}) => {
  const sessionDuration = sessionMinutesOrHours(session_length_minutes);
  const weeks = pluralize(week_count, 'week');

  if (week_count && session_per_week && session_length_minutes) {
    return `${weeks}, ${session_per_week}x/week, ${sessionDuration} each`;
  } else {
    return '';
  }
};

/**
  * Returns formatted 'next_class' string for UI
  *
  * @param {object} course object
  */
export const formattedNextClass = ({start_dates, next_class_date}) => {
  if (start_dates == undefined || start_dates.length === 0 || next_class_date == undefined) {
    return 'Check back soon';
  }

  const dateOnOrAfter = queryDate();
  const orderedStartDates = start_dates
    .map(start_date => new Date(start_date))
    .sort((a,b) => a - b);
  const nextClassDate = new Date(
    orderedStartDates.find(
      date => date >= dateOnOrAfter
    ) || next_class_date
  );

  return new Intl.DateTimeFormat(
    undefined,
    { weekday: 'short', month: 'short', day: 'numeric'}
  ).format(nextClassDate);
};

/**
  * Returns start-of-day date object for #on-or-after DOM element data-from attribute if present
  * Returns start-of-day date object for today if element missing or empty
  *
  */
const queryDate = () => {
  try {
    return new Date(
      document.querySelector('#on-or-after')
              .getAttribute('data-from'))
              .setHours(0,0,0,0);
  } catch (error) {
    return new Date().setHours(0,0,0,0);
  }
};

/**
  * Returns markdown formatted 'title' string for UI
  * prepends the instructor's name if the instructor is a celebrity
  *
  * @param {object} course object
  */
 export const formattedTitle = ({business_units, instructor_names, title_formatted, catalog_type, title_markdown}) => {
  if (catalog_type === 'catalog-topics-v1') {
    return Marked(`${title_markdown}`);
  } else if (business_units && business_units.indexOf('Celebrity') !== -1) {
    return Marked(`${instructor_names} - ${title_formatted}`);
  } else {
    return Marked(`${title_formatted}`);
  }
};

/**
  * Returns markdown formatted 'description' string for UI
  *
  * @param {object} course object
  */
 export const formattedDescription = ({description_formatted, catalog_type, description_markdown}) => {
  let description = '';
  if (catalog_type === 'catalog-topics-v1') {
    description = description_markdown || '';
  } else {
    description = description_formatted || '';
  }
  return Marked(`${description}`);
};

/**
  * Returns an array of list items for UI
  * If course's `price_cents` is 0 (free), the course size is Large
  *
  * @param {object} course object
  */
export const courseFeatures = ({price_cents, business_units}) => {
  if (price_cents === 0) {
    return ['Large-format, interactive', 'Free classes taught by experts'];
  } else {
    if (business_units && business_units.indexOf('School at Home') !== -1) {
      return ['Tailored to the students\' needs'];
    } else {
      return ['Average of 9 students/class', 'Tailored to the students\' needs'];
    }
  }
};

/*
 * This method only applies to school-at-home included courses
 * vt+ courses are handled differently
 */
export const courseIncluded = ({price_cents, subscriber, subjects}) => {
  const excludedSubjects = ['act', 'cpa', 'graduate test prep', 'gre', 'mcat', 'pmp', 'praxis',
    'professional certifications', 'professional development', 'sat', 'series 6', 'series 65',
    'series 7', 'sie', 'test prep', 'usmle'];
  if (subjects) return price_cents > 0 && schoolAtHomeMember({subscriber}) &&
                        !subjects.some(subj => excludedSubjects.includes(subj.toLowerCase()));
  return (price_cents > 0 && schoolAtHomeMember({subscriber}));
};

const vtPlusCourse = ({parent_group}) => {
  return !!(parent_group && parent_group.includes('category-vt+_course'));
};

const vtPlusMember = ({subscriber = ''}) => {
  return subscriber.split(',').includes('vt+');
};

const learningMemberCourse = ({parent_group}) => {
  return !!(parent_group && parent_group.includes('category-learning_member_course'));
};

const schoolAtHomeMember = ({subscriber = ''}) => {
  return subscriber.split(',').includes('school-at-home');
};

/*
 * This method returns true if the item tags matches with either of the student's
 * enablement tags with type catalog_access
 */
const isAnyStudentAMember = ({students, parent_group}) => {
 return students && students.find(student => activeEnablement({...student, parent_group: parent_group}));
};

/*
 * This method returns true if the item tags matches with any of the student's
 * enablement tags with type catalog_access
 */
const activeEnablement = ({enablements, parent_group}) => {
  if (enablements && parent_group) {
    return enablements.find(enablement => 
      enablement && enablement.product?.type === 'catalog_access' && parent_group.includes(enablement.configuration?.tag)
    );
  }
  return false;
};

/*
 * This method returns true if the recognized user is a student.
 */
const activeStudent = ({is_student}) => {
  return is_student;
};

/*
 * This method returns true if the recognized user is a client.
 */
const activeClient = ({is_client}) => {
  return is_client;
};

/* eslint-disable complexity */
export const displayPrice = (courseData) => {
  let response = '';

  let enablementPresent = (activeStudent(courseData) && activeEnablement(courseData)) ||
  (activeClient(courseData) && isAnyStudentAMember(courseData));
  if (enablementPresent) return 'Included with your membership';

  if (vtPlusCourse(courseData) && vtPlusMember(courseData)) {
    return  'Included with your VT+ Membership';
  }

  if (learningMemberCourse(courseData)) {
    // Add link to /membership/overview when the page is ready.
    // https://varsity.atlassian.net/browse/MEX-10050.
    return 'Free with ' + '<a href="/memberships">Learning Membership</a>';
  } else if (courseIncluded(courseData)) {
    response = `<del>${formattedPrice(courseData)}</del>&nbsp;Included
      <span>(Using your subscription hours)</span>`;
  } else {
    response = formattedPrice(courseData);
  }
  return response;
};
/* eslint-enable complexity */

/**
  * Returns grade range as string or combines array
  *
  * @param {object} course object
  */
 export const gradeFullRange = ({grade_full_range}) => {
  const gradesLen = grade_full_range?.length;
  if (Array.isArray(grade_full_range)) {
    let strippedRange = [];
    grade_full_range.forEach((range) => {
      const stripGrade = range.split('Grade ');
      const stripGrades = range.split('Grades ');
      const formattedRange = stripGrades[1] || stripGrade[1] || range;
      strippedRange.push(formattedRange);
    });

    let gradeDisplay = '';
    strippedRange.forEach((range, index) => {
      if (index === 0 ) {
        gradeDisplay += (range.includes('College') || range.includes('Adult')) ? range : `Grades ${range}` ;
      } else if (index < gradesLen - 1) {
        gradeDisplay += `, ${range}`;
      } else {
        gradeDisplay += ` and ${range}`;
      }
    });

    return gradeDisplay;
  } else {
    return grade_full_range || '';
  }
};

/**
  * Returns catalog type based on data attribute
  *
  * @param {object} course object
  */
 export const catalogType = ({catalog_type}) => {
  return catalog_type ===  'catalog-topics-v1' ? 'catalog-topics-v1' : 'catalog-items-v1';
};

/**
  * Maps ElasticSearch result object into an object for use by the mustache template
  *
  * @param {object} course
  */
const cardData = (course) => {
  const course_features = courseFeatures(course);
  const course_image_data = cardImageData(course);
  const curated_list_tags = curatedListTags(course);
  const formatted_duration = formattedDuration(course);
  const formatted_meeting = formattedMeeting(course);
  const formatted_next_class = formattedNextClass(course);
  const grade_full_range = gradeFullRange(course);
  const display_price = displayPrice(course);
  const formatted_title = formattedTitle(course);
  const formatted_description = formattedDescription(course);
  const formatted_weekly_duration = formattedWeeklyDuration(course);
  const small_group = course.price_cents > 0;
  const catalog_item = catalogType(course) === 'catalog-items-v1';
  const catalog_topic = catalogType(course) === 'catalog-topics-v1';
  const detail_link = course.details_page || course.topic_page;

  return {
    ...course_image_data,
    ...course,
    course_features,
    course_is_active: true,
    curated_list_tags,
    display_price,
    formatted_duration,
    formatted_description,
    formatted_meeting,
    formatted_next_class,
    formatted_title,
    formatted_weekly_duration,
    grade_full_range,
    included: courseIncluded(course),
    other_offerings: otherOfferings(course),
    small_group,
    catalog_topic,
    catalog_item,
    detail_link
  };
};

/**
  * Returns HTML from the mustache template
  *
  * @param {object} course
  * @param {string} name of component to render
  */
const render = (course, display) => {
  if(display == 'card'){
    return courseListCardUI(cardData(course));
  } else {
    return courseListItemUI(cardData(course));
  }
};

export default {
  render
};
